/**
 * Node customizado para Carrossel (Carousel Message)
 * Exibe preview dos cartões configurados com miniaturas
 * Inclui toolbar flutuante ao selecionar
 * 
 * RELEASE: 1.0.0 - 2026-02-08 - Novo node de carrossel multi-provider
 */

import { Handle, Position, NodeProps } from '@xyflow/react';
import { LayoutGrid, Image, Inbox } from 'lucide-react';
import { FlowNodeData, MessageCarouselConfig } from '@/types/flows';
import { FlowNodeToolbar } from './FlowNodeToolbar';
import { Badge } from '@/components/ui/badge';

/**
 * Componente visual do node de carrossel
 * Mostra preview horizontal dos cartões e contagem
 */
export function CarouselNode({ data, id, selected }: NodeProps) {
  const nodeData = data as FlowNodeData;
  const config = nodeData.config as MessageCarouselConfig | undefined;
  
  // Callbacks injetados via FlowCanvas
  const onEdit = (nodeData as any).onEdit;
  const onDuplicate = (nodeData as any).onDuplicate;
  const onDelete = (nodeData as any).onDelete;
  const onTestFromHere = (nodeData as any).onTestFromHere;
  
  // Dados do carrossel
  const message = config?.message;
  const cards = config?.cards || [];
  const inboxName = config?.inbox_name;
  
  // Preview do texto (truncado)
  const preview = message && message.length > 60 
    ? message.substring(0, 60) + '...' 
    : message;
  
  // Verifica se está configurado (pelo menos 1 cartão)
  const isConfigured = cards.length > 0;

  return (
    <div className="bg-card rounded-lg shadow-lg border-2 border-border min-w-[220px] max-w-[300px]">
      {/* Toolbar flutuante */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType={nodeData.type}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onTestFromHere={onTestFromHere}
        isVisible={selected}
      />
      
      <Handle
        type="target"
        position={Position.Top}
        className="!bg-border !w-3 !h-3"
      />
      
      <div className="p-3">
        {/* Header do node */}
        <div className="flex items-center gap-2 mb-2">
          <div className="bg-gradient-to-br from-amber-500 to-orange-500 p-2 rounded text-white">
            <LayoutGrid className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-sm text-foreground">
              {nodeData.config?.nickname || 'Carrossel'}
            </div>
            <div className="text-xs text-muted-foreground">
              {cards.length > 0 ? `${cards.length} cartão${cards.length > 1 ? 'ões' : ''}` : 'Não configurado'}
            </div>
          </div>
        </div>
        
        {/* Conteúdo configurado */}
        {isConfigured ? (
          <>
            {/* Preview horizontal dos cartões (miniaturas) */}
            <div className="flex gap-1 overflow-hidden mt-2 mb-2">
              {cards.slice(0, 4).map((card, index) => (
                <div 
                  key={card.id || index}
                  className="w-12 h-12 rounded bg-muted/50 border border-border flex items-center justify-center flex-shrink-0 overflow-hidden"
                  title={card.text || `Cartão ${index + 1}`}
                >
                  {card.image_url ? (
                    <img 
                      src={card.image_url} 
                      alt={`Cartão ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <Image className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
              ))}
              {cards.length > 4 && (
                <div className="w-12 h-12 rounded bg-muted/50 border border-border flex items-center justify-center flex-shrink-0">
                  <span className="text-xs text-muted-foreground font-medium">
                    +{cards.length - 4}
                  </span>
                </div>
              )}
            </div>
            
            {/* Preview da mensagem principal */}
            {preview && (
              <div className="text-xs text-muted-foreground bg-muted/50 rounded p-2 mb-2 truncate">
                {preview}
              </div>
            )}
            
            {/* Badge com contagem de botões */}
            <div className="flex flex-wrap gap-1.5">
              {cards.some(c => c.buttons && c.buttons.length > 0) && (
                <Badge 
                  variant="outline" 
                  className="text-[10px] px-1.5 py-0.5 bg-blue-500/10 text-blue-600 border-blue-500/20"
                >
                  Com botões
                </Badge>
              )}
            </div>
            
            {/* Inbox selecionada */}
            {inboxName && (
              <div className="flex items-center gap-1 mt-2 text-[10px] text-muted-foreground">
                <Inbox className="h-3 w-3" />
                <span className="truncate">{inboxName}</span>
              </div>
            )}
          </>
        ) : (
          /* Estado não configurado */
          <div className="text-xs text-muted-foreground italic bg-muted/30 rounded p-2 mt-2">
            Clique para configurar cartões
          </div>
        )}
      </div>
      
      <Handle
        type="source"
        position={Position.Bottom}
        className="!bg-border !w-3 !h-3"
      />
    </div>
  );
}
